// api.js (fetch-based replacement for wretch-based api file)

// Generic helpers ----------------------------------------------------------

async function parseResponse(res, expected = "json") {
  // expected: "json" | "text" | "blob"
  if (res.ok) {
    if (expected === "json") return res.json();
    if (expected === "text") return res.text();
    if (expected === "blob") return res.blob();
    return res;
  }

  // Try to parse error body (prefer JSON)
  let errBody;
  try {
    errBody = await res.json();
  } catch (e) {
    try {
      errBody = await res.text();
    } catch (e2) {
      errBody = null;
    }
  }

  const err = new Error("HTTP error");
  err.status = res.status;
  err.body = errBody;
  throw err;
}

function buildUrl(url, params) {
  if (!params || Object.keys(params).length === 0) return url;
  // Sanitize numeric values to prevent scientific notation in URL params
  const sanitized = {};
  for (const key of Object.keys(params)) {
    const val = params[key];
    sanitized[key] = typeof val === "number" && Number.isFinite(val) ? Math.floor(val).toString() : val;
  }
  return url + "?" + new URLSearchParams(sanitized).toString();
}

const DEFAULT_FETCH_OPTIONS = {
  credentials: "same-origin",
  headers: {}
};

async function fetchGet(url, params, options) {
  const finalUrl = buildUrl(url, params);
  const res = await fetch(finalUrl, {
    method: "GET",
    credentials: (options && options.credentials) || DEFAULT_FETCH_OPTIONS.credentials,
    headers: Object.assign({}, DEFAULT_FETCH_OPTIONS.headers, options && options.headers)
  });
  return res;
}

async function fetchJson(method, url, body, params, options) {
  const finalUrl = buildUrl(url, params);
  const headers = Object.assign(
    { "Content-Type": "application/json", Accept: "application/json" },
    options && options.headers
  );

  const res = await fetch(finalUrl, {
    method: method,
    credentials: (options && options.credentials) || DEFAULT_FETCH_OPTIONS.credentials,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  return res;
}

// Convenience wrappers that return parsed responses
async function getJson(url, params = {}, options = {}) {
  const res = await fetchGet(url, params, options);
  return parseResponse(res, "json");
}
async function getText(url, params = {}, options = {}) {
  const res = await fetchGet(url, params, options);
  return parseResponse(res, "text");
}
async function getBlob(url, params = {}, options = {}) {
  const res = await fetchGet(url, params, options);
  return parseResponse(res, "blob");
}
async function postJson(url, body = {}, params = {}, options = {}) {
  const res = await fetchJson("POST", url, body, params, options);
  return parseResponse(res, "json");
}
async function putJson(url, body = {}, params = {}, options = {}) {
  const res = await fetchJson("PUT", url, body, params, options);
  return parseResponse(res, "json");
}
async function postText(url, body = {}, params = {}, options = {}) {
  const res = await fetchJson("POST", url, body, params, options);
  return parseResponse(res, "text");
}

// Exported API functions ---------------------------------------------------

// Equivalent of getRequest(url, params) in original file
export function getRequest(url, params) {
  return getJson(url, params);
}

// storyPageLoadItems
export async function storyPageLoadItems(pageNumber, mostPopular) {
  console.log("Called storyPageLoadItems ");
  const FIELDS =
    "id,headline,subheadline,metadata,slug,url,hero-image-s3-key,tags,hero-image-metadata,first-published-at,last-published-at,alternative,published-at,authors,sections,story-template,cards";

  const params = {
    fields: FIELDS,
    limit: 5,
    offset: parseInt(5 * pageNumber, 10) || 0
  };

  const data = await getJson("/api/v1/stories", params);
  // keep shape: map to { story, mostPopular }
  return (
    (data &&
      data.stories &&
      data.stories.map(function(story) {
        return { story: story, mostPopular: mostPopular };
      })) ||
    []
  );
}

// loadNextStories
export const loadNextStories = async pageNumber => {
  const FIELDS = "id,slug,first-published-at,last-published-at";

  const params = {
    fields: FIELDS,
    limit: 20
  };

  const data = await getJson("/api/v1/collections/latest-news", params);
  if (!data || !data.items) return [];
  return data.items
    .filter(function(i) {
      return i.type === "story";
    })
    .map(function(it) {
      return { slug: it.story.slug, id: it.story.id };
    });
};

// bottomStickyTopStory
export const bottomStickyTopStory = async () => {
  try {
    const data = await getJson("/api/v1/collections/main");
    if (data && data.items && data.items.length) return data.items[0].story;
    return null;
  } catch (error) {
    console.log("error", error);
    return null;
  }
};

// getUser
export function getUser() {
  return getJson("/api/auth/v1/users/me");
}

// logout
export async function logout() {
  // original used .res() to resolve; here we call endpoint and ignore body
  const res = await fetchGet("/api/auth/v1/logout");
  // return resolved promise (no value)
  if (!res.ok) {
    return parseResponse(res, "json").catch(function(e) {
      throw e;
    });
  }
  return;
}

// updateUserInformation
export async function updateUserInformation(data) {
  try {
    await postJson("/api/auth/v1/users/profile", data);
    return;
  } catch (ex) {
    throw ex;
  }
}

// loginUser
export async function loginUser(email, password) {
  try {
    const body = {
      username: email,
      password: password
    };
    const res = await postJson("/api/auth/v1/login", body, {}, { headers: { "Content-Type": "application/json" } });
    return res;
  } catch (ex) {
    throw ex;
  }
}

// signUpUser
export async function signUpUser(options) {
  try {
    const res = await postJson("/api/auth/v1/signup", options, {}, { headers: { "Content-Type": "application/json" } });
    return res;
  } catch (ex) {
    throw ex;
  }
}

// forgotPassword
export async function forgotPassword(email) {
  try {
    const res = await postJson(
      "/api/auth/v1/users/forgot-password",
      { email },
      {},
      { headers: { "Content-Type": "application/json" } }
    );
    return res;
  } catch (ex) {
    throw ex;
  }
}

// resetPassword
export async function resetPassword(token, password, repeatPassword) {
  try {
    const res = await postJson(
      "/api/auth/v1/users/reset-password-with-token",
      {
        token: token,
        "new-password": password,
        "confirm-password": repeatPassword
      },
      {},
      { headers: { "Content-Type": "application/json" } }
    );
    return res;
  } catch (ex) {
    // try to preserve original behavior where it parsed ex.message
    if (ex && ex.body) {
      // if body contains error
      if (typeof ex.body === "object" && ex.body.error) {
        throw ex.body.error;
      }
      // otherwise if body is text
      throw ex.body;
    }
    throw ex;
  }
}

// updateUserMetadata
export async function updateUserMetadata(data) {
  try {
    await postJson("/api/member/metadata", { metadata: data }, {}, { headers: { "Content-Type": "application/json" } });
    return;
  } catch (ex) {
    throw ex;
  }
}

// generateJwtToken
export async function generateJwtToken(email, id, name) {
  try {
    const res = await postJson(
      "/generate-token",
      { email: email, id: id, name: name },
      {},
      { headers: { "Content-Type": "application/json" } }
    );
    return res && res.token;
  } catch (ex) {
    throw ex;
  }
}

// sendSupportEmail
export async function sendSupportEmail(data) {
  try {
    const res = await postJson("/support-mail", data, {}, { headers: { "Content-Type": "application/json" } });
    return res;
  } catch (ex) {
    throw ex;
  }
}

// sendSubscribeUserNPEmail
export async function sendSubscribeUserNPEmail(data) {
  try {
    const res = await postJson("/subscribe-user", data, {}, { headers: { "Content-Type": "application/json" } });
    return res;
  } catch (ex) {
    throw ex;
  }
}

// sendDigitalSubscribeUserEmail
export async function sendDigitalSubscribeUserEmail(data) {
  try {
    const res = await postJson(
      "/digital-subscribe-user",
      data,
      {},
      { headers: { "Content-Type": "application/json" } }
    );
    return res;
  } catch (ex) {
    throw ex;
  }
}

// sendSubscribeUserToAdminEmail
export async function sendSubscribeUserToAdminEmail(data) {
  try {
    const res = await postJson("/subscription", data, {}, { headers: { "Content-Type": "application/json" } });
    return res;
  } catch (ex) {
    throw ex;
  }
}

// getPageCommentsData
export async function getPageCommentsData(accountId, url) {
  const encodedUrl = btoa(url);
  try {
    const res = await getJson("https://www.metype.com/api/v1/accounts/" + accountId + "/pages/" + encodedUrl);
    return res;
  } catch (ex) {
    throw ex;
  }
}

// submitNewsLetter
export const submitNewsLetter = (id, token, body) => {
  const params = { id: id, token: token };
  // original returned text
  return postText("/newsletter", body, params).catch(function(e) {
    throw e;
  });
};

// sendTransactionalEmail
export const sendTransactionalEmail = options => {
  return postJson("/transactional-emails", options, {}, { headers: { "Content-Type": "application/json" } }).catch(
    function(e) {
      throw e;
    }
  );
};

// getCountry
export const getCountry = async () => {
  try {
    const data = await getJson("https://ipinfo.io/json?token=06e346377eff84");
    return data;
  } catch (err) {
    console.log(err);
    return null;
  }
};

// getAccessToken
export const getAccessToken = async () => {
  try {
    // In original code .post().text()
    const res = await fetchJson(
      "POST",
      "/zoho-billing-access-token",
      {},
      {},
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        }
      }
    );
    const text = await parseResponse(res, "text");
    localStorage.setItem("AToken", text);
    return text;
  } catch (error) {
    console.error("Error during API call:", error);
    throw error;
  }
};

// createNewCustomer
export const createNewCustomer = async (name, email, fullName, number, address, city, state, zip, gender) => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const body = {
      name: name,
      email: email,
      accessToken: token,
      number: number,
      fullName: fullName,
      address: address,
      city: city,
      state: state,
      zip: zip,
      gender: gender
    };
    const res = await postJson(
      "/zoho-create-customer",
      body,
      {},
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        }
      }
    );
    // res is parsed JSON
    var customerId = BigInt(res.customer_id).toString();
    localStorage.setItem("activeDigitalSub", false);
    return customerId;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};

// updateExistingCustomer
export const updateExistingCustomer = async (
  customerId,
  name,
  email,
  number,
  fullName,
  address,
  city,
  state,
  zip,
  country,
  gender,
  dob,
  currency,
  pricebookID
) => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const body = {
      customerId: customerId,
      name: name,
      email: email,
      accessToken: token,
      number: number,
      fullName: fullName,
      address: address,
      city: city,
      state: state,
      zip: zip,
      country: country,
      gender: gender,
      dob: dob,
      currency: currency,
      pricebookID: pricebookID
    };
    const res = await putJson(
      "/zoho-update-customer",
      body,
      {},
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        }
      }
    );
    var updatedCustomerId = BigInt(res.customer_id).toString();
    return updatedCustomerId;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};

// getPaymentUrl
export const getPaymentUrl = async (customerID, zohoPlanSelected, customValue = false) => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const body = {
      customerID: customerID,
      accessToken: token,
      zohoPlanSelected: zohoPlanSelected,
      customValue: customValue
    };
    const res = await postJson(
      "/zoho-payment-url",
      body,
      {},
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        }
      }
    );
    return res;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};

// existingCustomerCheck
export const existingCustomerCheck = async (name, email) => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const params = { email: email, accessToken: token };
    const res = await getJson("/zoho-existing-customer-check", params, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      }
    });
    if (res && res.customers && res.customers.length === 1) {
      return res.customers;
    } else {
      return "new";
    }
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return null;
    }
  }
};

// downloadInvoicePDF
export const downloadInvoicePDF = async invID => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const params = { invID: invID, accessToken: token };
    const blob = await getBlob("/zoho-get-invoice-pdf", params, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      }
    });
    // Create URL for the blob and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "invoice_" + invID + ".pdf");
    document.body.appendChild(link);
    link.click();
    link.remove();
    // revoke object URL after a short timeout
    setTimeout(function() {
      window.URL.revokeObjectURL(url);
    }, 10000);
    return true;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during PDF download:", error);
      alert("Failed to download the invoice. Please try again later.");
      return false;
    }
  }
};

// cancelZohoSub
export const cancelZohoSub = async subID => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const body = { subID: subID, accessToken: token };
    const res = await postJson(
      "/zoho-cancel-subscription",
      body,
      {},
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        }
      }
    );
    return res;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during subscription cancellation:", error);
      return false;
    }
  }
};

// getInvoiceNumber
export const getInvoiceNumber = async subID => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const params = { subID: subID, accessToken: token };
    const res = await getJson("/zoho-get-invoice-number", params, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      }
    });
    return (res && res.subscription && res.subscription.child_invoice_id) || null;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};

// getZohoUserData
export const getZohoUserData = async userID => {
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const params = { userID: userID, accessToken: token };
    const res = await getJson("/zoho-retrieve-customer", params, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      }
    });
    return (res && res.customer) || null;
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};

// existingSubscriptionCheck
export const existingSubscriptionCheck = async (customerID, zohoPlanSelected, planStatus) => {
  if (typeof planStatus === "undefined" || planStatus === null) planStatus = "ACTIVE";
  let accessToken = localStorage.getItem("AToken");

  async function call(token) {
    const params = { customerID: customerID, accessToken: token, planStatus: planStatus };
    const res = await getJson("/zoho-subscription-check", params, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0"
      }
    });

    if (!res || !res.subscriptions) return false;

    if (zohoPlanSelected === "all") {
      if (res.subscriptions.length > 0) return res.subscriptions;
      return false;
    } else {
      var planCodes = Array.isArray(zohoPlanSelected) ? zohoPlanSelected : [zohoPlanSelected];
      if (res.subscriptions.length > 0) {
        var matchingSubscriptions = res.subscriptions.filter(function(sub) {
          return planCodes.indexOf(sub.plan_code) !== -1;
        });
        if (matchingSubscriptions.length > 0) return matchingSubscriptions;
      }
      return false;
    }
  }

  try {
    return await call(accessToken);
  } catch (error) {
    if (error && error.status === 401) {
      console.error("Token not correct, unauthorized.");
      var newToken = await getAccessToken();
      return await call(newToken);
    } else {
      console.error("Error during API call:", error);
      return false;
    }
  }
};
